/**
 * drawer-section.js
 */

import React from 'react';
import { useMediaQuery, Typography, IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Switch, useParams } from 'react-router-dom';
import { shouldRenderMobileLayout, shouldRenderMobileInDesktopDrawer } from 'utils/app-context';
import { UserContext } from 'contexts/user/user-context';
import Route from 'routing/route';
import EditButton from 'components/edit-button';
import Icon from 'components/icon';
import DrawerContent from 'components/menu-drawer/drawer-content';
import { Types } from 'utils/constants';


const useStyles = makeStyles(theme => ({
  header: {
    justifyContent: 'space-between',
    height: theme.spacing(7),
    marginTop: theme.spacing(3),
    padding: `0 ${theme.spacing(3)}`,
    backgroundColor: theme.palette.sys.surfaceContainer,
    borderBottom: `1px solid transparent`,
    borderRadius: 4,
    // cursor: 'pointer',
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    fontWeight: 'normal',
    transition: `200ms ease-in-out`,
    '&.open': {
      borderBottom: `1px solid ${theme.palette.sys.outlineVariant}`,
      borderRadius: `4px 4px 0 0`,
    },
    '&.edit': {
      backgroundColor: theme.palette.primary.colors[5],
      color: theme.palette.sys.onPrimary,
      borderBottom: 0,
      borderRadius: 4,
      position: 'sticky',
      top: theme.spacing(1),
      bottom: theme.spacing(1),
      boxShadow: theme.shadows[3],
      zIndex: 400,
      cursor: 'default',
    },
    '&.disabled': {
      opacity: 0.6,
      cursor: 'default'
    },
    // tablet
    [theme.breakpoints.down('md')]: {
      borderBottom: 0,
      backgroundColor: theme.palette.sys.surfaceContainerHigh,
      '&.open': {
        borderBottom: 0,
      },
      '&.first-section': {
        marginTop: theme.spacing(2)
      },
    },
    // drawer breakpoint
    [theme.breakpoints.down('sm_md')]: {
      '&.first-section': {
        marginTop: theme.spacing(1)
      },
    },
    // phone
    [theme.breakpoints.down('sm')]: {
      padding: `0 ${theme.spacing(1.5)}`,
      '& .title': {
        fontSize: theme.typography.sizes[2],
        lineHeight: 1.2
      }
    },
  },
  rightContainer: {
    whiteSpace: 'nowrap',
  },
  iconButton: {
    color: 'inherit',
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      padding: theme.spacing(1),
    },
  },
  editButton: {
    marginLeft: theme.spacing(1),
  },
  expanderIcon: {
    marginLeft: theme.spacing(1),
    transition: '150ms ease-in-out',
    '&.open': {
      transform: 'rotateZ(180deg)',
    },
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      marginLeft: theme.spacing(0.5),
    },
  },
  button: {
    border: `2px dashed ${theme.palette.grey.colors[8]}`,
  },
  detail: {
    paddingTop: theme.spacing(3),
    paddingBottom: 1,
    backgroundColor: theme.palette.sys.surfaceContainer,
    borderRadius: `0 0 4px 4px`,
    overflow: 'hidden',
    '&.edit': {
      position: 'relative',
      top: -3,
      borderColor: theme.palette.primary.colors[5],
      borderStyle: 'solid',
      borderWidth: '0px 3px 3px',
    },
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      paddingTop: 0
    },
  },
}));


export default function DrawerSection(props) {
  const {
    deploymentId,
    sectionPath,
    sectionType,
    patrolIndex,
    label,
    drawerState,
    basePath,
    edit,
    routes,
    stepChip,
    onPatrolDelete,
    disabled,
    firstSection,
    ...rest
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { editSectionId, viewSectionId } = useParams();
  const [open, setOpen] = React.useState(!disabled);
  const [editActions, setEditActions] = React.useState();
  const [isContentRendered, setIsContentRendered] = React.useState(true);
  const scrollTarget = React.useRef();
  const isEditing = editSectionId && editSectionId === sectionPath;
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), { noSsr: true });
  const isSelectedSection = viewSectionId === sectionPath;

  React.useEffect(() => {
    if (isEditing) {
      setOpen(isContentRendered);
    }
  }, [isEditing, isContentRendered]);

  // Scroll to the top of the section when it is marked for Editing
  React.useEffect(() => {
    if (isEditing && open) {
      const timeoutId = setTimeout(() => {
        if (scrollTarget.current) {
          scrollTarget.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'  // Ensures it scrolls to the top of the element
          });
        }
      }, 250); // Increased timeout to ensure content is rendered
      return () => clearTimeout(timeoutId);
    }
  }, [isEditing, open]);

  // Scroll to the section when it is selected from the menu (viewSectionId) is set
  React.useEffect(() => {
    if (isSelectedSection && open) {
      // Scroll to the section  
      if (scrollTarget.current) {
        scrollTarget.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'  // Ensures it scrolls to the top of the element
        }); 
      }
    }
  }, [isSelectedSection, open]);

  const patrolIndexSuffix = patrolIndex !== undefined ? `-${patrolIndex}` : '';
  const headerContent = (
    <>
      <div className='flex-hc'>
        {stepChip}

        {/* label */}
        <Typography variant='subtitle1' className='title'>
          {label}
        </Typography>
      </div>

      <div className={`flex-hc ${classes.rightContainer}`}>
        
        {/* delete button */}
        {onPatrolDelete 
          && (!isEditing || (!shouldRenderMobileLayout() && !shouldRenderMobileInDesktopDrawer()))
          && user.role.isHandler &&
        <Tooltip 
          arrow 
          title={user.role.status.isActive ? 'Delete this patrol type' : ''}
          placement='top'
        >
          <span>
            <IconButton
              className={classes.iconButton}
              onClick={e => {
                e.stopPropagation();
                onPatrolDelete();
              }}
              disabled={!user.role.status.isActive}
              size="large"
              data-testid={`deployment-section-delete-button${patrolIndexSuffix}`}
            >
              <Icon type='delete' />
            </IconButton>
          </span>
        </Tooltip>}

        {/* edit actions */}
        {isEditing ? editActions : ''}

        {/* expand indicator */}
        {/* {!isEditing && !disabled &&
        <Icon 
          type='expander' 
          className={`${classes.expanderIcon} ${open ? 'open' : ''}`} 
        />} */}

        {/* edit button */}
        {!isEditing && !disabled && user.role.isHandler &&
          <EditButton 
            className={classes.editButton}
            tooltip={user.role.status.isActive ? 'Edit this section' : ''}
            iconOnly={isMobileDrawer}
            to={edit.location}
            onClick={e => e.stopPropagation()}
            disabled={!user.role.status.isActive}
            data-testid={`section-edit-button-${sectionType}${patrolIndexSuffix}`}
          />
        }

      </div>
    </>
  );


  const header = (
    <div 
      className={`section-header `
        + `flex-hc `
        + `${classes.header} `
        + `${open ? 'open' : ''} `
        + `${isEditing ? 'edit' : ''} `
        + `${disabled ? 'disabled' : ''}`
        + `${firstSection ? 'first-section' : ''}`} 
      // onClick={isEditing || disabled ? null : () => setOpen(p => !p)}
    >
      {headerContent}
    </div>
  );


  const content = !disabled && (
    <div style={{display: open ? 'block' : 'none'}}>
      <div 
        className={`drawer-section `
          + `${classes.detail} ${open ? 'open' : ''} `
          + `${isEditing ? 'edit' : ''}`}
        data-testid={`drawer-section-${sectionType}${sectionType === Types.DeploymentSection.PATROL ? '-' + patrolIndex : ''}`}
      >
        <DrawerContent>
          <Switch>
            {routes
              .filter(r => r.component)
              .map((route, i) => 
                <Route
                  key={i}
                  path={route.path}
                  render={() => 
                    <route.component 
                      deploymentId={deploymentId}
                      sectionPath={sectionPath}
                      sectionType={sectionType}
                      basePath={basePath}
                      drawerState={drawerState}
                      editSectionId={editSectionId}
                      onPatrolDelete={onPatrolDelete}
                      patrolIndex={patrolIndex}
                      edit={{
                        ...edit,
                        setActions: setEditActions,
                        setIsContentRendered,
                      }}
                      {...rest}
                    />}
                />)}
          </Switch>
        </DrawerContent>
      </div>
    </div>
  );


  return (
    <div style={{ position: 'relative', top: -16, width: '100%' }}>
      {/* scroll position */}
      <div ref={scrollTarget} style={{ position: 'relative', top: -16 }} />

      {header}

      {content}
    </div>
  );
}